import type {
  NoticeTemplate,
  NoticeTemplateStep,
  TemplateStepPayload,
} from "@/features/notices/types";
import { apiFetch } from "@/lib/api";

/**
 * Firm notice step-template endpoints (Settings › Notice Templates), under
 * `/firm/notice-templates/`, keyed by the `reference.NoticeDefinition` code.
 * Reads are open to any firm user; writes require a firm administrator (the
 * backend enforces it — the UI also gates the controls).
 */
interface Envelope<T> {
  success: boolean;
  message: string;
  data: T;
}

interface ApiTemplateStep {
  id: string;
  description: string;
  link: string;
  position: number;
}

interface ApiTemplate {
  id: string | null;
  notice_definition: string;
  code: string;
  title: string;
  severity: string;
  category: string;
  description: string;
  recommended_action: string;
  guidance: string;
  steps: ApiTemplateStep[];
}

function toStep(a: ApiTemplateStep): NoticeTemplateStep {
  return { id: a.id, description: a.description, link: a.link, position: a.position };
}

function toTemplate(a: ApiTemplate): NoticeTemplate {
  return {
    id: a.id,
    noticeDefinition: a.notice_definition,
    code: a.code,
    title: a.title,
    severity: a.severity,
    category: a.category,
    description: a.description,
    recommendedAction: a.recommended_action,
    guidance: a.guidance,
    steps: a.steps.map(toStep),
  };
}

const BASE = "/firm/notice-templates";

/** GET /firm/notice-templates/:code/ — the firm's overlay for one code (an
 * unsaved empty shape if none exists yet). */
export async function getNoticeTemplate(code: string): Promise<NoticeTemplate> {
  const body = await apiFetch<Envelope<ApiTemplate>>(`${BASE}/${code}/`);
  return toTemplate(body.data);
}

/** PATCH /firm/notice-templates/:code/ — save the firm guidance notes. */
export async function updateTemplateGuidance(
  code: string,
  guidance: string,
): Promise<NoticeTemplate> {
  const body = await apiFetch<Envelope<ApiTemplate>>(`${BASE}/${code}/`, {
    method: "PATCH",
    json: { guidance },
  });
  return toTemplate(body.data);
}

export async function addTemplateStep(
  code: string,
  payload: TemplateStepPayload,
): Promise<NoticeTemplateStep> {
  const body = await apiFetch<Envelope<ApiTemplateStep>>(`${BASE}/${code}/steps/`, {
    method: "POST",
    json: payload,
  });
  return toStep(body.data);
}

export async function updateTemplateStep(
  code: string,
  id: string,
  payload: TemplateStepPayload,
): Promise<NoticeTemplateStep> {
  const body = await apiFetch<Envelope<ApiTemplateStep>>(
    `${BASE}/${code}/steps/${id}/`,
    { method: "PATCH", json: payload },
  );
  return toStep(body.data);
}

export function deleteTemplateStep(code: string, id: string): Promise<void> {
  return apiFetch<void>(`${BASE}/${code}/steps/${id}/`, { method: "DELETE" });
}

/** POST /firm/notice-templates/:code/steps/reorder/ — `ids` in the new order. */
export async function reorderTemplateSteps(
  code: string,
  ids: string[],
): Promise<NoticeTemplateStep[]> {
  const body = await apiFetch<Envelope<ApiTemplateStep[]>>(
    `${BASE}/${code}/steps/reorder/`,
    { method: "POST", json: { ids } },
  );
  return body.data.map(toStep);
}

/** POST /firm/notice-templates/:code/copy-from/:sourceCode/ — replace this
 * code's steps with a copy of the source code's steps. */
export async function copyTemplateSteps(
  code: string,
  sourceCode: string,
): Promise<NoticeTemplate> {
  const body = await apiFetch<Envelope<ApiTemplate>>(
    `${BASE}/${code}/copy-from/${sourceCode}/`,
    { method: "POST" },
  );
  return toTemplate(body.data);
}
